import React from 'react';
import { path } from 'ramda';
import { useHistory } from 'react-router';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { ACTION_CODES } from 'constants/errors';
import { TIER_BENEFIT_UPGRADE_CODE, TIER_BENEFIT_STATUS_USED } from 'constants/tierBenefit';
import { BUSINESS_BENEFIT_STATUS_USED, BUSINESS_BENEFIT_UPGRADE_CODES } from 'constants/businessBenefitUpgrade';
import AgentPortalError from 'errors/AgentPortalError';
import { getBookingJourneysURL } from 'utils/urls';
import { FullPageSpinner } from 'admin-components/components';
import { useDefaultedCurrencyFromURL } from 'utils/hooks/currency';
import { GET_LOYALTY_MEMBER_BENEFITS, GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS } from 'store/queries';
import config from 'utils/config';

import { GET_BOOKING, GET_USER, OVERRIDE_FEES } from './graphql';

import Refunds from './Refunds';
import HotelRefundWrapper from './HotelRefundWrapper';

const getActiveOrProvisionallCancelledHotels = (booking) => booking.hotels.concat(booking.provisionallyCancelledHotels);

const IS_TIER_BENEFIT_UPGRADE_ENABLED = config('ENABLE_TIER_BENEFIT_UPGRADE', { canOverride: true }) === 'true';
const IS_BUSINESS_BENEFIT_UPGRADE_ENABLED = config('ENABLE_BUSINESS_ACCOUNT_BENEFIT_UPGRADE', { canOverride: true }) === 'true';

const RefundsContainer = ({ reference, isMultiCurrency, bookingCurrency }) => {
  const history = useHistory();

  const userState = useQuery(GET_USER);

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');

  const user = path(['data', 'me'], userState);

  const [currency, changeCurrency] = useDefaultedCurrencyFromURL({
    availableCurrencies: user.paymentCurrencies,
    defaultCurrency: bookingCurrency,
    isMultiCurrency
  });

  const [overrideFees, overrideFeesState] = useMutation(OVERRIDE_FEES, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const bookingState = useQuery(GET_BOOKING, {
    variables: { reference, currency },
    fetchPolicy: 'network-only'
  });

  const booking = path(['data', 'booking'], bookingState);

  const amountCancelledProductsWithTierBenefit = booking?.pendingCancellationLegs?.reduce((acc, { id, feeProducts }) => {
    const creationLeg = (booking?.pendingCreationLegs ?? []).find(({ id: legId }) => legId === id);

    return (
      acc +
      ((feeProducts ?? []).filter(({ code }) => code === TIER_BENEFIT_UPGRADE_CODE).length -
        (creationLeg?.feeProducts ?? []).filter(({ code }) => code === TIER_BENEFIT_UPGRADE_CODE).length)
    );
  }, 0);

  const hasTierBenefitUpgradeProducts = amountCancelledProductsWithTierBenefit > 0;

  const amountCancelledProductsWithBusinessBenefit = booking?.pendingCancellationLegs?.reduce((acc, { id, products }) => {
    const creationLeg = (booking?.pendingCreationLegs ?? []).find(({ id: legId }) => legId === id);

    return (
      acc +
      ((products ?? []).filter(({ code }) => BUSINESS_BENEFIT_UPGRADE_CODES.includes(code)).length -
        (creationLeg?.products ?? []).filter(({ code }) => BUSINESS_BENEFIT_UPGRADE_CODES.includes(code)).length)
    );
  }, 0);

  const hasBusinessBenefitUpgradeProducts = amountCancelledProductsWithBusinessBenefit > 0;

  const userTierBenefitState = useQuery(GET_LOYALTY_MEMBER_BENEFITS, {
    variables: { membershipNumber },
    fetchPolicy: 'network-only',
    skip: !membershipNumber || !hasTierBenefitUpgradeProducts || !IS_TIER_BENEFIT_UPGRADE_ENABLED
  });

  const userBusinessBenefitState = useQuery(GET_LOYALTY_BUSINESS_ACCOUNT_BENEFITS, {
    variables: { membershipNumber },
    fetchPolicy: 'network-only',
    skip: !membershipNumber || !hasBusinessBenefitUpgradeProducts || !IS_BUSINESS_BENEFIT_UPGRADE_ENABLED
  });

  const state = [userState, bookingState, overrideFeesState, userTierBenefitState, userBusinessBenefitState];
  const error = state.map((item) => item.error).find(Boolean);
  const loading = state.some((item) => item.loading);

  const userTierBenefitUpgrades = (userTierBenefitState?.data?.loyaltyMemberBenefits ?? [])
    .filter(
      ({ benefitStatus, bookingReference, startDate, endDate }) =>
        benefitStatus === TIER_BENEFIT_STATUS_USED &&
        bookingReference === reference &&
        moment().isBetween(moment(startDate), moment(endDate))
    )
    .sort((a, b) => a.endDate.localeCompare(b.endDate))
    .slice(0, amountCancelledProductsWithTierBenefit);

  const userBusinessBenefitUpgrades = (userBusinessBenefitState?.data?.getLoyaltyBusinessAccountBenefits?.benefitList ?? [])
    .filter(
      ({ benefitStatus, pnr, startDate, endDate }) =>
        benefitStatus === BUSINESS_BENEFIT_STATUS_USED && pnr === reference && moment().isBetween(moment(startDate), moment(endDate))
    )
    .sort((a, b) => a.endDate.localeCompare(b.endDate))
    .slice(0, amountCancelledProductsWithBusinessBenefit);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getBookingJourneysURL(reference)
    });
  }

  if (loading) {
    return <FullPageSpinner />;
  }

  const hotels = getActiveOrProvisionallCancelledHotels(booking);
  const canHotelBeCancelledOnThirdParty = hotels.some((h) => h.canBeCancelledOnThirdParty);

  return (
    <>
      {canHotelBeCancelledOnThirdParty ? (
        <HotelRefundWrapper booking={booking} hotels={hotels}>
          <Refunds
            overrideFees={overrideFees}
            currency={currency}
            booking={booking}
            hotels={hotels}
            user={user}
            onCurrencyChange={changeCurrency}
            membershipNumber={membershipNumber}
            hasTierBenefitUpgradeProducts={hasTierBenefitUpgradeProducts}
            userTierBenefitUpgrades={userTierBenefitUpgrades}
            hasBusinessBenefitUpgradeProducts={hasBusinessBenefitUpgradeProducts}
            userBusinessBenefitUpgrades={userBusinessBenefitUpgrades}
          />
        </HotelRefundWrapper>
      ) : (
        <Refunds
          overrideFees={overrideFees}
          currency={currency}
          booking={booking}
          hotels={hotels}
          user={user}
          onCurrencyChange={changeCurrency}
          membershipNumber={membershipNumber}
          hasTierBenefitUpgradeProducts={hasTierBenefitUpgradeProducts}
          userTierBenefitUpgrades={userTierBenefitUpgrades}
          hasBusinessBenefitUpgradeProducts={hasBusinessBenefitUpgradeProducts}
          userBusinessBenefitUpgrades={userBusinessBenefitUpgrades}
        />
      )}
    </>
  );
};

RefundsContainer.propTypes = {
  reference: PropTypes.string.isRequired,
  isMultiCurrency: PropTypes.bool.isRequired,
  bookingCurrency: PropTypes.string.isRequired
};

export default RefundsContainer;
